// Prospects → Import uploader. On submit it runs the presigned three-step
// flow, then opens the page the confirm names:
//   1. POST /prospects/imports              → the import and a presigned PUT
//   2. PUT bytes directly to the presigned URL
//   3. POST /prospects/imports/{id}/confirm → the page to open next
// presigned_upload.js runs the requests.

import { onceGuard } from "./bind_once.js";
import { contentTypeOf, postForm, putFile } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-import-upload]";
const FALLBACK_ERROR = "Could not upload your file. Please try again.";
const NO_FILE_ERROR = "Choose a CSV or Excel file to import.";
const BUSY_STATUS = "Uploading your file…";

export function initImportUpload(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireForm);
}

// A page the back-forward cache restores keeps the busy state its submit
// left, so clear it.
window.addEventListener("pageshow", () => {
  document
    .querySelectorAll(ROOT_SELECTOR)
    .forEach((form) => setBusy(form, false));
});

function wireForm(form) {
  if (!firstBind(form)) return;

  const input = form.querySelector('input[type="file"]');
  const errorEl = form.querySelector("[data-import-error]");
  const submit = form.querySelector("[data-import-submit]");

  // A new file clears the last one's refusal.
  input?.addEventListener("change", () => hide(errorEl));

  form.addEventListener("submit", async (event) => {
    event.preventDefault();

    const file = input?.files?.[0];
    if (!file) {
      showError(errorEl, NO_FILE_ERROR);
      return;
    }

    hide(errorEl);
    setBusy(form, true);

    try {
      window.location.assign(await uploadOne(file));
    } catch (err) {
      console.error("import upload failed", err);
      setBusy(form, false);
      showError(errorEl, err?.userMessage || FALLBACK_ERROR);
      submit?.focus({ preventScroll: true });
    }
  });
}

async function uploadOne(file) {
  const started = await postForm("/prospects/imports", {
    file_name: file.name,
    content_type: contentTypeOf(file),
    size_bytes: String(file.size),
  });
  if (!started.import_id || !started.upload_url) {
    throw new Error("import upload: create answered no URL");
  }

  await putFile(started.upload_url, file, started.upload_headers);

  const confirmed = await postForm(
    `/prospects/imports/${encodeURIComponent(started.import_id)}/confirm`,
  );
  if (!confirmed.next)
    throw new Error("import upload: confirm answered no page");

  return confirmed.next;
}

// setBusy holds the form while a file uploads: the button shows its
// spinner, the picker can't swap the file mid-flight, and a screen
// reader hears the wait.
function setBusy(form, busy) {
  const submit = form.querySelector("[data-import-submit]");
  const picker = form.querySelector("[data-file-upload]");
  const status = form.querySelector("[data-import-upload-status]");

  if (submit) {
    submit.disabled = busy;
    submit.setAttribute("aria-busy", String(busy));
  }
  if (picker) picker.inert = busy;
  if (status) status.textContent = busy ? BUSY_STATUS : "";
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hide(el) {
  if (!el) return;
  el.textContent = "";
  el.classList.add("hidden");
}
